import { useState } from "react";
import { AlertTriangle, Building2, CheckCircle2, FileUp, Link2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const ClientCell = ({ request }) =>
  request.client_id ? (
    <span data-testid={`request-client-${request.id}`}>{request.client_name}</span>
  ) : (
    <span className="client-missing-cell" data-testid={`request-client-${request.id}`}>
      <AlertTriangle size={13} /> No client linked.
    </span>
  );

export const BulkClientBar = ({ clientOptions, count, onApplied, onClear, selectedIds }) => {
  const [clientId, setClientId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  async function apply() {
    setIsSaving(true);
    setError("");
    try {
      const response = await api.post("/requests/bulk-client", { request_ids: [...selectedIds], client_id: clientId });
      onApplied(response.data);
      setClientId("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="bulk-action-panel request-bulk-bar" data-testid="bulk-client-bar">
      <div>
        <Link2 size={15} />
        <strong data-testid="bulk-selected-count">{count}</strong> selected · assign a client
      </div>
      <select data-testid="bulk-client-select" value={clientId} onChange={(event) => setClientId(event.target.value)}>
        <option value="">Choose client…</option>
        {clientOptions.map((client) => (
          <option key={client.id} value={client.id}>
            {client.name}
          </option>
        ))}
      </select>
      <button className="button button-primary" data-testid="bulk-client-apply-button" disabled={!clientId || isSaving} type="button" onClick={apply}>
        <Building2 size={15} /> {isSaving ? "Assigning…" : `Assign to ${count}`}
      </button>
      <button className="clear-filter-button request-bulk-clear" data-testid="bulk-clear-selection-button" type="button" onClick={onClear}>
        <X size={14} /> Clear
      </button>
      {error && <span className="request-bulk-error" data-testid="bulk-client-error">{error}</span>}
    </div>
  );
};

const ROW_LABELS = {
  ready: "Ready",
  updated: "Updated",
  unchanged: "Unchanged",
  unknown_request: "Unknown request",
  unknown_client: "Unknown client",
  invalid: "Invalid row",
};

export const ClientCsvImportPanel = ({ onApplied, onClose }) => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  async function upload(apply) {
    setIsBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("apply", apply ? "true" : "false");
      const response = await api.post("/requests/client-import", body, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(response.data);
      if (apply) {
        onApplied(response.data);
      }
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <section className="inline-edit-panel csv-import-panel" data-testid="client-csv-import-panel">
      <div className="section-heading">
        <div>
          <h2>Import client assignments</h2>
          <p>CSV with two columns: <code>request_id</code> and <code>client</code> (client name or ID). Existing request IDs only.</p>
        </div>
        <button aria-label="Close import panel" className="projection-icon-button" data-testid="close-client-csv-import" type="button" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      {error && <p className="form-message error-message" data-testid="client-csv-error">{error}</p>}
      <div className="csv-import-controls">
        <label className="csv-file-picker">
          <FileUp size={15} /> {file ? file.name : "Choose CSV file"}
          <input accept=".csv,text/csv" data-testid="client-csv-file-input" type="file" onChange={(event) => { setFile(event.target.files?.[0] || null); setResult(null); }} />
        </label>
        <button className="button button-secondary" data-testid="client-csv-preview-button" disabled={!file || isBusy} type="button" onClick={() => upload(false)}>
          Preview
        </button>
        <button className="button button-primary" data-testid="client-csv-apply-button" disabled={!result || !result.dry_run || !result.ready || isBusy} type="button" onClick={() => upload(true)}>
          <CheckCircle2 size={15} /> Apply {result?.dry_run ? `${result.ready} update${result.ready === 1 ? "" : "s"}` : ""}
        </button>
      </div>
      {result && (
        <div className="csv-import-result" data-testid="client-csv-result">
          <p className={`form-message ${result.dry_run ? "csv-preview-message" : "success-message"}`} data-testid="client-csv-summary">
            {result.dry_run
              ? `${result.total} row${result.total === 1 ? "" : "s"} read · ${result.ready} ready to update · ${result.total - result.ready} skipped`
              : `${result.applied} request${result.applied === 1 ? "" : "s"} linked to their client.`}
          </p>
          <div className="csv-preview-wrap">
            <table className="csv-preview-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Request ID</th>
                  <th>Client</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row) => (
                  <tr key={row.row} data-testid={`csv-row-${row.row}`}>
                    <td>{row.row}</td>
                    <td><strong>{row.request_id || "—"}</strong></td>
                    <td>{row.client_name || row.client_value || "—"}</td>
                    <td>
                      <span className={`csv-row-status csv-row-${row.status}`}>{ROW_LABELS[row.status]}</span>
                      <small>{row.message}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};
